import DATA from './data/swrTaxProfiles.json';

export const SWR_TAX_DATA_VERSION = DATA.version;
export const SWR_TAX_DATA_HASH = DATA.contentSha256;
export const SWR_FLOW_ACCOUNTING_VERSION = 'italian-per-isin-flow-rebalance-v1';
export const SWR_FLOW_METHOD_VERSION = 'joint-blocks-12m-flow-rebalance-tax-v3';
export const SWR_DEBT_METHOD_VERSION = 'joint-blocks-12m-flow-tax-variable-debt-v4';
export const SWR_STANDARD_SALES_POLICY = 'asset-id-within-rebalance-level-v1';
export const SWR_LOSS_FIRST_SALES_POLICY = 'loss-first-proportional-grossup-v1';
export const SWR_LOSS_FIRST_ACCOUNTING_VERSION = 'italian-per-isin-loss-first-v2';
export const getSWRSalesPolicy = tax => tax?.salesPolicy ?? SWR_STANDARD_SALES_POLICY;
export const isSWRLossFirstPolicy = params => isSWRFlowTaxPortfolio(params)
  && getSWRSalesPolicy(params.historicalPortfolio.taxation) === SWR_LOSS_FIRST_SALES_POLICY;
export const getSWRSalesPolicyLabel = tax => getSWRSalesPolicy(tax) === SWR_LOSS_FIRST_SALES_POLICY
  ? 'Perdite prima dei guadagni compensabili' : 'Ordine standard';

// Standard deliberately omits the optional key: old saved inputs retain their meaning.
export function withSWRSalesPolicy(config, policy) {
  const taxation = { ...config.taxation };
  if (policy === SWR_STANDARD_SALES_POLICY) delete taxation.salesPolicy;
  else taxation.salesPolicy = policy;
  return { ...config, taxation };
}
export const SWR_TAX_PROFILES = DATA.profiles;
export const SWR_TAX_SOURCES = DATA.sources;
const profiles = new Map(DATA.profiles.map(p => [p.assetId, Object.freeze(p)]));
// Same locale ordering as before, computed once for the immutable catalogue.
// Unknown IDs retain the original comparator for diagnostics outside the catalogue.
const assetOrder = Object.assign(Object.create(null), Object.fromEntries(
  DATA.profiles.map(p => p.assetId).sort((a, b) => a.localeCompare(b)).map((id, i) => [id, i])));
const compareAssetOrder = (a, b) => {
  const rankA = assetOrder[a.assetId], rankB = assetOrder[b.assetId];
  return rankA === undefined || rankB === undefined ? a.assetId.localeCompare(b.assetId) : rankA - rankB;
};
const EPS = 1e-10;

export const getSWRTaxProfile = assetId => profiles.get(assetId) || null;
export const isSWRFlowTaxPortfolio = params => params?.historicalPortfolio?.enabled === true
  && [SWR_FLOW_METHOD_VERSION, SWR_DEBT_METHOD_VERSION].includes(params.historicalPortfolio.methodVersion);
export const isSWRDebtPortfolio = params => params?.historicalPortfolio?.enabled === true
  && params.historicalPortfolio.methodVersion === SWR_DEBT_METHOD_VERSION;
export const getSWRSimpleTaxRate = equityFraction => equityFraction * .26
  + (1 - equityFraction) * getSWRTaxProfile('bond-global-aggregate').baseRate
    * getSWRTaxProfile('bond-global-aggregate').taxableFraction;
export const createSWRSimpleTaxProfile = equityPct => ({ datasetVersion: SWR_TAX_DATA_VERSION,
  datasetSha256: SWR_TAX_DATA_HASH, equityPct, equityRate: .26,
  bondProxyISIN: 'IE00BDBRDM35', bondProxyRate: getSWRTaxProfile('bond-global-aggregate').baseRate
    * getSWRTaxProfile('bond-global-aggregate').taxableFraction, convention: 'allocation-weighted-aggh-fiscal-proxy-v1' });

export function createSWRTaxConfig(options = {}) {
  return { datasetVersion: SWR_TAX_DATA_VERSION, datasetSha256: SWR_TAX_DATA_HASH,
    regime: DATA.regime, initialBasisByAsset: {}, basisReferenceCapital: null,
    initialLosses: [], ...options };
}

// Unknown editions are rejected for calculation; imported snapshots remain readable.
export function normalizeSWRTaxConfig(config, allocations, maximumBasis = 1e15) {
  if (!config || config.datasetVersion !== SWR_TAX_DATA_VERSION || config.datasetSha256 !== SWR_TAX_DATA_HASH) {
    throw new Error('Il registro fiscale di questo scenario non è disponibile. Nessuna sostituzione automatica delle aliquote.');
  }
  if (config.regime !== DATA.regime) throw new Error('Il modello fiscale richiede il regime amministrato italiano per persona fisica.');
  if (![SWR_STANDARD_SALES_POLICY, SWR_LOSS_FIRST_SALES_POLICY].includes(getSWRSalesPolicy(config))) {
    throw new Error('Politica fiscale delle vendite non disponibile. Nessuna sostituzione automatica.');
  }
  const basis = config.initialBasisByAsset;
  if (!basis || typeof basis !== 'object' || Array.isArray(basis)) throw new Error('Basi fiscali per strumento non valide.');
  const ids = new Set(allocations.filter(a => a.weight > 0).map(a => a.assetId));
  for (const [id, value] of Object.entries(basis)) {
    if (!ids.has(id) || !Number.isFinite(value) || value < 0 || value > maximumBasis) throw new Error('Costo fiscale iniziale non valido per una delle asset class.');
  }
  if (Object.keys(basis).length && (!Number.isFinite(config.basisReferenceCapital) || config.basisReferenceCapital <= 0)) {
    throw new Error('Manca il capitale di riferimento per scalare le basi fiscali iniziali.');
  }
  const losses = config.initialLosses;
  if (!Array.isArray(losses) || losses.length > 20 || losses.some(l => !Number.isFinite(l?.amount)
    || l.amount < 0 || l.amount > 1e15 || !Number.isInteger(l.expiryYear) || l.expiryYear < 1900 || l.expiryYear > 2500)) {
    throw new Error('Minusvalenze pregresse non valide: indica importo fiscalmente riconosciuto e anno di scadenza.');
  }
  if (allocations.some(a => !getSWRTaxProfile(a.assetId))) throw new Error('Profilo fiscale dello strumento non disponibile.');
  return { ...createSWRTaxConfig(), ...config, initialBasisByAsset: { ...basis },
    initialLosses: losses.filter(l => l.amount > 0).map(l => ({ amount: l.amount, expiryYear: l.expiryYear }))
      .sort((a, b) => a.expiryYear - b.expiryYear) };
}

export function getSWRTaxCalendarYear(params, monthIndex = 0) {
  if (!params.valuationDate) throw new Error('La fiscalità per strumento richiede una data di riferimento per le scadenze civili.');
  const [year, month] = params.valuationDate.split('-').map(Number);
  const wait = params.planningMode === 'future' || params.futurePlanningEnabled ? params.yearsUntilStart || 0 : 0;
  return year + wait + Math.floor((month - 1 + monthIndex) / 12);
}

export function createSWRTaxLedger(params, initialInvested) {
  const config = params.historicalPortfolio;
  const leverage = isSWRDebtPortfolio(params) ? config.allocations.reduce((s, a) => s + a.weight, 0) : 1;
  const tax = normalizeSWRTaxConfig(config.taxation, config.allocations, 1e15 * leverage);
  return {
    salesPolicy: getSWRSalesPolicy(tax),
    positions: config.allocations.filter(a => a.weight > 0).map(a => {
      const value = initialInvested * a.weight;
      const basis = Object.hasOwn(tax.initialBasisByAsset, a.assetId)
        ? tax.initialBasisByAsset[a.assetId] * initialInvested / tax.basisReferenceCapital
        : value * (1 - params.initialGainFraction);
      return { assetId: a.assetId, profile: getSWRTaxProfile(a.assetId), targetWeight: a.weight / leverage,
        value, basis, quantity: value, unitValue: 1, totalTax: 0, totalGrossSales: 0,
        totalRealizedGain: 0, totalRecognizedLoss: 0 };
    }).sort(compareAssetOrder),
    losses: tax.initialLosses.map(l => ({ ...l })), totalLossUsed: 0, totalLossExpired: 0,
  };
}

export const getSWRTaxLedgerValue = ledger => ledger.positions.reduce((s, p) => s + p.value, 0);
export const getSWRTaxLedgerBasis = ledger => ledger.positions.reduce((s, p) => s + p.basis, 0);

export function expireSWRTaxLosses(ledger, year) {
  ledger.losses = ledger.losses.filter(l => {
    if (l.expiryYear >= year && l.amount > EPS) return true;
    ledger.totalLossExpired += Math.max(l.amount, 0);
    return false;
  });
}

function addLoss(ledger, amount, year) {
  if (amount <= EPS) return;
  const previous = ledger.losses.find(l => l.expiryYear === year + 4);
  if (previous) previous.amount += amount;
  else ledger.losses.push({ amount, expiryYear: year + 4 });
  ledger.losses.sort((a, b) => a.expiryYear - b.expiryYear);
}

function consumeLoss(ledger, gain) {
  let used = 0;
  for (const loss of ledger.losses) {
    const take = Math.min(loss.amount, gain - used);
    loss.amount -= take; used += take;
    if (used >= gain) break;
  }
  ledger.totalLossUsed += used;
  return used;
}

function sellPosition(ledger, p, gross, year) {
  gross = Math.min(gross, p.value);
  if (gross <= 0 || p.value <= 0) return { tax: 0, net: 0, gross: 0 };
  const fraction = Math.min(1, gross / p.value);
  const removedBasis = p.basis * fraction;
  const gain = gross - removedBasis;
  const taxableGain = Math.max(gain, 0) * p.profile.taxableFraction;
  const offset = p.profile.incomeType === 'redditi-diversi' ? consumeLoss(ledger, taxableGain) : 0;
  const tax = (taxableGain - offset) * p.profile.baseRate;
  const recognizedLoss = Math.max(-gain, 0) * p.profile.taxableFraction;
  addLoss(ledger, recognizedLoss, year);
  p.quantity *= 1 - fraction; p.basis *= 1 - fraction; p.value *= 1 - fraction;
  p.totalTax += tax; p.totalGrossSales += gross; p.totalRealizedGain += Math.max(gain, 0);
  p.totalRecognizedLoss += recognizedLoss;
  return { tax, net: gross - tax, gross };
}

// A level contains tied value/target-weight ratios. Transactions within it use a
// fixed asset-id order or the explicitly selected loss-first order. For this catalog
// only gold ETC offsets gains; the net proceeds have at most one tax breakpoint.
function financeLevel(ledger, level, grossAmounts, targetNet, year, reserveFraction) {
  let baseNet = 0, eligibleGain = 0, priorLoss = 0, beforeEligible = true;
  let eligibleRate = 0;
  const credit = ledger.losses.reduce((s, l) => s + l.amount, 0);
  for (let i = 0; i < level.length; i++) {
    const p = level[i], gross = grossAmounts[i];
    const gain = p.value > 0 ? gross * (1 - p.basis / p.value) * p.profile.taxableFraction : 0;
    if (p.profile.incomeType === 'redditi-diversi' && gain > 0) {
      if (!beforeEligible) throw new Error('Il risolutore fiscale richiede al massimo un ETC compensabile.');
      eligibleGain = gain; eligibleRate = p.profile.baseRate; beforeEligible = false;
    } else if (gain < 0 && beforeEligible) priorLoss -= gain;
    baseNet += gross - (p.profile.incomeType === 'redditi-capitale' ? Math.max(gain, 0) * p.profile.baseRate : 0) - gross * reserveFraction;
  }
  const excessGain = Math.max(eligibleGain - priorLoss, 0);
  const breakpoint = excessGain > 0 ? credit / excessGain : Infinity;
  const fullNet = baseNet - eligibleRate * Math.max(excessGain - credit, 0);
  // At very high *effective* debt ratios, tax after a credit breakpoint can
  // exceed the remaining equity share. Fund a feasible earlier fraction first;
  // the unlevered solver's existing branch and arithmetic remain unchanged.
  const maximumNet = reserveFraction > 0
    ? Math.max(0, fullNet, Math.min(1, breakpoint) * baseNet) : fullNet;
  let fraction = 1;
  if (targetNet < fullNet || (reserveFraction > 0 && baseNet > 0 && targetNet <= maximumNet)) {
    fraction = targetNet <= breakpoint * baseNet ? targetNet / baseNet
      : (targetNet - eligibleRate * credit) / (baseNet - eligibleRate * excessGain);
  }
  fraction = Math.min(1, Math.max(0, fraction));
  let net = 0, tax = 0, gross = 0;
  for (let i = 0; i < level.length; i++) {
    const sale = sellPosition(ledger, level[i], grossAmounts[i] * fraction, year);
    net += sale.net - sale.gross * reserveFraction; tax += sale.tax; gross += sale.gross;
  }
  return { netReceived: net, tax, grossWithdrawal: gross };
}

export function sellSWRToRebalance(ledger, targetNet, year, reserveFraction = 0) {
  if (!Number.isFinite(targetNet) || targetNet < 0) throw new Error('Fabbisogno netto fiscale non valido.');
  if (!Number.isFinite(reserveFraction) || reserveFraction < 0 || reserveFraction > 1) throw new Error('Quota di rimborso del debito non valida.');
  expireSWRTaxLosses(ledger, year);
  const result = { netReceived: 0, tax: 0, grossWithdrawal: 0 };
  if (targetNet === 0) return result;
  closeSWRWorthlessPositions(ledger, year);
  const sorted = ledger.positions.filter(p => p.value > 0).sort((a, b) => b.value / b.targetWeight - a.value / a.targetWeight || compareAssetOrder(a, b));
  let size = 0;
  while (size < sorted.length && result.netReceived < targetNet) {
    size++;
    const high = sorted[size - 1].value / sorted[size - 1].targetWeight;
    const next = size < sorted.length ? sorted[size].value / sorted[size].targetWeight : 0;
    if (high <= next) continue;
    const level = sorted.slice(0, size).sort((a, b) => {
      // Only change execution order within this already eligible level. The
      // gross-up then reduces its common fraction to fund the same net amount.
      if (ledger.salesPolicy === SWR_LOSS_FIRST_SALES_POLICY) {
        const lossA = a.basis > a.value ? 0 : 1;
        const lossB = b.basis > b.value ? 0 : 1;
        if (lossA !== lossB) return lossA - lossB;
      }
      return compareAssetOrder(a, b);
    });
    const grossAmounts = level.map(p => Math.max(0, p.value - next * p.targetWeight));
    const sale = financeLevel(ledger, level, grossAmounts, targetNet - result.netReceived, year, reserveFraction);
    result.netReceived += sale.netReceived; result.tax += sale.tax; result.grossWithdrawal += sale.grossWithdrawal;
    if (result.netReceived >= targetNet - Math.max(1e-8, targetNet * 1e-12)) break;
  }
  return result;
}

export function buySWRUnderweights(ledger, amount, year = null) {
  if (!Number.isFinite(amount) || amount < 0) throw new Error('Apporto investito non valido.');
  if (amount > 0) closeSWRWorthlessPositions(ledger, year);
  const sorted = [...ledger.positions].sort((a, b) => a.value / a.targetWeight - b.value / b.targetWeight || compareAssetOrder(a, b));
  let remaining = amount;
  for (let size = 1; size <= sorted.length && remaining > 0; size++) {
    const level = sorted.slice(0, size);
    const high = size < sorted.length ? sorted[size].value / sorted[size].targetWeight : Infinity;
    const needed = level.reduce((s, p) => s + Math.max(0, high * p.targetWeight - p.value), 0);
    const spend = Math.min(remaining, needed), weight = level.reduce((s, p) => s + p.targetWeight, 0);
    for (const p of level) {
      const purchase = spend * p.targetWeight / weight;
      p.value += purchase; p.basis += purchase; p.quantity += purchase / p.unitValue;
    }
    remaining -= spend;
  }
}

export function applySWRComponentGrowth(ledger, growthFactors, commonFactor = 1, aggregateStress = null) {
  const before = getSWRTaxLedgerValue(ledger);
  const weights = before > 0 ? ledger.positions.map(p => p.value / before)
    : ledger.marketWeights || ledger.positions.map(p => p.targetWeight);
  let baseFactor = 0;
  for (let a = 0; a < ledger.positions.length; a++) baseFactor += weights[a] * growthFactors[a] * commonFactor;
  const factor = aggregateStress == null ? commonFactor
    : baseFactor > 0 ? commonFactor * (1 + aggregateStress) / baseFactor
    : commonFactor;
  if (!Number.isFinite(factor) || factor < 0) throw new Error('Trasformazione dello stress per strumento non valida.');
  for (let a = 0; a < ledger.positions.length; a++) {
    const p = ledger.positions[a], growth = growthFactors[a] * factor;
    if (!Number.isFinite(growth) || growth < 0) throw new Error('Rendimento per strumento non valido.');
    p.unitValue *= growth; p.value *= growth;
  }
  const marketFactor = baseFactor * factor / commonFactor;
  if (marketFactor > 0) ledger.marketWeights = weights.map((w, a) => w * growthFactors[a] * factor / marketFactor);
  return marketFactor - 1;
}

// Only exact total-loss synthetic scenarios reach zero unit value. Dispose the
// worthless holding at zero proceeds before a new purchase; retain loss expiry.
export function closeSWRWorthlessPositions(ledger, year) {
  for (const p of ledger.positions) if (p.unitValue === 0) {
    if (!Number.isInteger(year)) throw new Error('Manca l’anno fiscale per liquidare una posizione azzerata.');
    const loss = p.basis * p.profile.taxableFraction;
    addLoss(ledger, loss, year); p.totalRecognizedLoss += loss;
    p.basis = 0; p.quantity = 0; p.value = 0; p.unitValue = 1;
  }
}

export function getSWRTaxLedgerSnapshot(ledger, year, cash = 0) {
  const value = getSWRTaxLedgerValue(ledger);
  const cloned = { ...ledger, positions: ledger.positions.map(p => ({ ...p })), losses: ledger.losses.map(l => ({ ...l })) };
  const liquidation = sellSWRToRebalance(cloned, Number.MAX_VALUE, year);
  return { invested: value, cash, costBasis: getSWRTaxLedgerBasis(ledger), netLiquidationValue: cash + liquidation.netReceived,
    hypotheticalLiquidationTax: liquidation.tax, lossAvailable: ledger.losses.reduce((s, l) => s + l.amount, 0),
    lossUsed: ledger.totalLossUsed, lossExpired: ledger.totalLossExpired,
    positions: ledger.positions.map(p => ({ assetId: p.assetId, isin: p.profile.isin,
      targetWeight: p.targetWeight, actualWeight: value > 0 ? p.value / value : null,
      value: p.value, quantity: p.quantity, unitValue: p.unitValue, costBasis: p.basis,
      tax: p.totalTax, grossSales: p.totalGrossSales, recognizedLoss: p.totalRecognizedLoss,
      realizedGain: p.totalRealizedGain })) };
}
